/**
 * Going away ([D-022](../../../docs/DECISIONS.md#d-022),
 * [D-133](../../../docs/DECISIONS.md#d-133), mockup 18).
 *
 * A child off to camp keeps their streak safe *on purpose*, for free. The
 * rules live in [`core`](../../../packages/core/src/vacation.ts) and the server
 * enforces them; this screen only has to make them easy to say yes to and
 * kind when they say no.
 *
 * Two choices: how long, and when it started. "When" is there because real
 * life does not announce itself — a child who fell ill on Tuesday can still
 * say so on Thursday, and one who was away a week without telling anyone can
 * still cover the days just missed, as long as they do it before their next
 * lesson.
 */

import '@capytalk/tokens/blocks/quiz.css';
import { useCallback, useEffect, useState } from 'react';
import {
  VACATION_DAYS_PER_YEAR,
  type VacationProblem,
  addDays,
  daysBetween,
} from '@capytalk/core';
import { type Client, today as localToday } from './api.js';
import Sneakers from './Sneakers.js';

type Status = Awaited<ReturnType<Client['vacation']['status']['query']>>;
type Holiday = NonNullable<Status['current']>;

/** What to say when the answer is no. Never a scolding, always a way on. */
export const PROBLEMS: Record<VacationProblem, string> = {
  'bad-date': 'That date does not look right. Try another one.',
  'bad-length': 'A holiday can be from 1 to 21 days.',
  'too-far-back': 'That is too far back. Pick a later day.',
  'too-far-ahead': 'That is a long way off! Come back nearer the time.',
  overlaps: 'You already have a holiday on those days.',
  'over-allowance': 'You have used up your holiday days for this year.',
};

/** "Friday 25 September", in the words a child reads on a calendar. */
export const spoken = (day: string): string =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

const SINCE = 'Since I last played';

/** The start days on offer: the last few, and the days just missed if further. */
export function startChoices(
  day: string,
  earliest: string,
): readonly { readonly startDay: string; readonly label: string }[] {
  const recent = [
    { startDay: day, label: 'Today' },
    { startDay: addDays(day, -1), label: 'Yesterday' },
    { startDay: addDays(day, -2), label: '2 days ago' },
    { startDay: addDays(day, -3), label: '3 days ago' },
  ].filter((c) => c.startDay >= earliest);
  const oldest = recent.at(-1)?.startDay ?? day;
  return earliest < oldest ? [...recent, { startDay: earliest, label: SINCE }] : recent;
}

const DEFAULT_DAYS = 7;

export default function HolidayScreen({
  client,
  onLeave,
}: {
  readonly client: Client;
  readonly onLeave: () => void;
}) {
  const day = localToday();
  const [status, setStatus] = useState<Status | null>(null);
  const [trouble, setTrouble] = useState(false);
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [startDay, setStartDay] = useState(day);
  const [refused, setRefused] = useState<VacationProblem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setTrouble(false);
    client.vacation.status
      .query({ today: day })
      .then(setStatus)
      .catch(() => {
        setTrouble(true);
      });
  }, [client, day]);

  useEffect(load, [load]);

  const most =
    status === null ? 1 : Math.max(1, Math.min(status.maxDays, status.allowanceLeft));
  const length = Math.min(days, most);
  const choices = status === null ? [] : startChoices(day, status.earliestStart);
  const chosen = choices.some((c) => c.startDay === startDay) ? startDay : day;

  const goAway = () => {
    setBusy(true);
    setRefused(null);
    client.vacation.start
      .mutate({ today: day, startDay: chosen, days: length })
      .then((result) => {
        if (result.ok) load();
        else setRefused(result.problem);
      })
      .catch(() => {
        setTrouble(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const end = (holiday: Holiday) => {
    setBusy(true);
    client.vacation.end
      .mutate({ today: day, vacationId: holiday.vacationId })
      .then(setStatus)
      .catch(() => {
        setTrouble(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const leave = (label: string) => (
    <button
      type="button"
      className="btn ghost"
      style={{ marginTop: 10 }}
      onClick={onLeave}
    >
      {label}
    </button>
  );

  if (trouble) {
    return (
      <div className="pad grow">
        <h2>Going away?</h2>
        <p className="bubble" role="alert" style={{ marginTop: 12 }}>
          That did not work. Check the internet and try again.
        </p>
        <button type="button" className="btn" style={{ marginTop: 14 }} onClick={load}>
          Try again
        </button>
        {leave('Back')}
      </div>
    );
  }

  if (status === null) {
    return (
      <div className="pad grow">
        <h2>Going away?</h2>
        <p className="sub">One moment…</p>
      </div>
    );
  }

  const { current } = status;
  if (current !== null) {
    const lastDay = current.endDay === day;
    return (
      <div className="pad grow">
        <h2>On holiday</h2>
        <Sneakers
          text={
            lastDay
              ? 'Last holiday day! See you tomorrow?'
              : 'Have a lovely time! Your streak is safe while you are away.'
          }
          audioRef={null}
          size={56}
        />
        <div className="card center" style={{ marginTop: 14, padding: 20 }}>
          <div style={{ fontSize: 40 }} aria-hidden="true">
            🏖️
          </div>
          <p style={{ fontWeight: 800, marginTop: 8 }}>
            Back on {spoken(addDays(current.endDay, 1))}
          </p>
          <p className="sub">You can still play! It won&rsquo;t use up a day.</p>
        </div>
        <button
          type="button"
          className="btn navy"
          style={{ marginTop: 16 }}
          disabled={busy}
          onClick={() => {
            end(current);
          }}
        >
          I&rsquo;m back — end it now
        </button>
        {leave('Back')}
      </div>
    );
  }

  // Back the day after it ends, which is what a child says: "back on Saturday".
  const backOn = addDays(chosen, length);

  return (
    <div className="pad grow">
      <h2>Going away?</h2>
      <Sneakers
        text="Tell me how long, and I'll keep your streak safe."
        audioRef={null}
        size={56}
      />

      <div className="card center" style={{ marginTop: 14, padding: 20 }}>
        <div style={{ fontSize: 40 }} aria-hidden="true">
          🏖️
        </div>
        <div
          className="row"
          style={{ gap: 16, marginTop: 12, justifyContent: 'center' }}
        >
          <button
            type="button"
            className="tile"
            style={{ fontSize: 22, padding: '8px 16px' }}
            aria-label="One day fewer"
            disabled={length <= 1}
            onClick={() => {
              setDays(Math.max(1, length - 1));
            }}
          >
            −
          </button>
          <div aria-live="polite">
            <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}>{length}</div>
            <div className="tiny" style={{ fontWeight: 800 }}>
              {length === 1 ? 'day' : 'days'}
            </div>
          </div>
          <button
            type="button"
            className="tile"
            style={{ fontSize: 22, padding: '8px 16px' }}
            aria-label="One day more"
            disabled={length >= most}
            onClick={() => {
              setDays(Math.min(most, length + 1));
            }}
          >
            +
          </button>
        </div>
        <div className="tiny" style={{ marginTop: 12 }}>
          Back on {spoken(backOn)}
        </div>
      </div>

      <div className="kicker" style={{ margin: '14px 0 6px' }}>
        WHEN DID IT START?
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {choices.map((c) => (
          <button
            key={c.startDay}
            type="button"
            className={chosen === c.startDay ? 'chip on' : 'chip'}
            aria-pressed={chosen === c.startDay}
            onClick={() => {
              setStartDay(c.startDay);
              // The days just missed, up to yesterday: they are back today.
              if (c.label === SINCE)
                setDays(daysBetween(c.startDay, day) ?? DEFAULT_DAYS);
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="lrow">
          <span className="ic" aria-hidden="true">
            🔥
          </span>
          <span className="tx">Your streak stays safe</span>
        </div>
        <div className="lrow">
          <span className="ic" aria-hidden="true">
            🌰
          </span>
          <span className="tx">Free — this never costs acorns</span>
        </div>
        <div className="lrow">
          <span className="ic" aria-hidden="true">
            📅
          </span>
          <span className="tx">
            {status.allowanceLeft} of your {VACATION_DAYS_PER_YEAR} holiday days left
            this year
          </span>
        </div>
        <div className="lrow" style={{ border: 0 }}>
          <span className="ic" aria-hidden="true">
            🎓
          </span>
          <span className="tx">You can still play! It won&rsquo;t use up a day</span>
        </div>
      </div>

      {refused !== null && (
        <p className="bubble" role="status" style={{ marginTop: 10 }}>
          {PROBLEMS[refused]}
        </p>
      )}

      {status.upcoming.map((holiday) => (
        <div key={holiday.vacationId} className="card" style={{ marginTop: 12 }}>
          <p style={{ fontWeight: 700 }}>
            Planned: {spoken(holiday.startDay)} to {spoken(holiday.endDay)}
          </p>
          <button
            type="button"
            className="btn ghost"
            style={{ marginTop: 8 }}
            disabled={busy}
            onClick={() => {
              end(holiday);
            }}
          >
            Cancel it
          </button>
        </div>
      ))}

      <button
        type="button"
        className="btn navy"
        style={{ marginTop: 16 }}
        disabled={busy || status.allowanceLeft === 0}
        onClick={goAway}
      >
        Start my holiday
      </button>
      {leave('Not now')}
    </div>
  );
}
